import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useApi from '../utils/AxiosWrapper';
import {
  MilesChatMessage,
  MilesChatConversation,
  MilesChatPreset,
  SendMilesChatMessageResponse,
  GetMilesChatMessageStatusResponse,
  GetMilesChatConversationsResponse,
  GetMilesChatMessagesResponse,
  GetMilesChatPresetsResponse,
} from '../components/miles/milesTypes';

const MILES_API_BASE = '/miles';
const POLLING_INTERVAL = 1000; // 1 second
const MAX_POLLING_ATTEMPTS = 180; // 3 minutes max

interface UseMilesChatOptions {
  customerId: string | null;
  enabled?: boolean;
}

interface UseMilesChatReturn {
  // State
  messages: MilesChatMessage[];
  conversations: MilesChatConversation[];
  presetPrompts: MilesChatPreset[];
  currentConversationId: string | null;
  isLoading: boolean;
  isSending: boolean;
  isPolling: boolean;
  error: string | null;

  // Actions
  sendMessage: (message: string) => Promise<void>;
  selectConversation: (conversationId: string) => void;
  startNewConversation: () => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  clearError: () => void;
}

export function useMilesChat(options: UseMilesChatOptions): UseMilesChatReturn {
  const { customerId, enabled = true } = options;
  const isEnabled = enabled && !!customerId;
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();

  // Local state
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(null);
  const [localMessages, setLocalMessages] = useState<MilesChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Polling state ref
  const pollingRef = useRef<{
    messageId: string | null;
    attempts: number;
    timeoutId: ReturnType<typeof setTimeout> | null;
  }>({
    messageId: null,
    attempts: 0,
    timeoutId: null,
  });

  // Fetch conversations
  const { data: conversationsData, isLoading: isLoadingConversations } =
    useQuery<GetMilesChatConversationsResponse>({
      queryKey: ['miles-conversations', customerId],
      queryFn: async () => {
        const response = await callProtectedApi(
          `${MILES_API_BASE}/customers/${customerId}/conversations`,
          'get',
          {},
          'Error fetching conversations.'
        );
        return response?.data?.data;
      },
      enabled: isEnabled,
      staleTime: 30 * 1000, // 30 seconds
    });

  // Fetch preset prompts
  const { data: presetsData, isLoading: isLoadingPresets } =
    useQuery<GetMilesChatPresetsResponse>({
      queryKey: ['miles-presets', customerId],
      queryFn: async () => {
        const response = await callProtectedApi(
          `${MILES_API_BASE}/customers/${customerId}/presets`,
          'get',
          {},
          'Error fetching preset prompts.'
        );
        return response?.data?.data;
      },
      enabled: isEnabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });

  // Fetch messages for current conversation
  const { data: conversationMessagesData, isLoading: isLoadingMessages } =
    useQuery<GetMilesChatMessagesResponse>({
      queryKey: ['miles-messages', customerId, currentConversationId],
      queryFn: async () => {
        if (!currentConversationId)
          return { conversation_id: '', messages: [], total_count: 0 };
        const response = await callProtectedApi(
          `${MILES_API_BASE}/customers/${customerId}/conversations/${currentConversationId}/messages`,
          'get',
          {},
          'Error fetching messages.'
        );
        return response?.data?.data;
      },
      enabled: isEnabled && !!currentConversationId,
      staleTime: 10 * 1000, // 10 seconds
    });

  // Update local messages when conversation messages change
  useEffect(() => {
    if (conversationMessagesData?.messages) {
      setLocalMessages(conversationMessagesData.messages);
    }
  }, [conversationMessagesData]);

  // Poll for message status
  const pollMessageStatus = useCallback(
    async (
      messageId: string
    ): Promise<GetMilesChatMessageStatusResponse | null> => {
      if (!customerId) return null;
      try {
        const response = await callProtectedApi(
          `${MILES_API_BASE}/customers/${customerId}/messages/${messageId}/status`,
          'get',
          {},
          undefined,
          undefined,
          undefined,
          undefined,
          true // hide alert
        );
        return response?.data?.data;
      } catch {
        return null;
      }
    },
    [callProtectedApi, customerId]
  );

  // Start polling for a message
  const startPolling = useCallback(
    (messageId: string, conversationId: string) => {
      setIsPolling(true);
      pollingRef.current.messageId = messageId;
      pollingRef.current.attempts = 0;

      const poll = async () => {
        if (pollingRef.current.messageId !== messageId) return;
        if (pollingRef.current.attempts >= MAX_POLLING_ATTEMPTS) {
          setIsPolling(false);
          setError('Response timed out. Please try again.');
          // Update message to show error
          setLocalMessages((prev) =>
            prev.map((m) =>
              m.message_id === messageId
                ? {
                    ...m,
                    status: 'failed' as const,
                    content: 'Response timed out. Please try again.',
                  }
                : m
            )
          );
          return;
        }

        pollingRef.current.attempts++;

        const status = await pollMessageStatus(messageId);

        if (!status) {
          // Network error, retry
          pollingRef.current.timeoutId = setTimeout(poll, POLLING_INTERVAL);
          return;
        }

        if (status.status === 'completed' || status.status === 'failed') {
          // Update the message with the response
          setLocalMessages((prev) =>
            prev.map((m) =>
              m.message_id === messageId
                ? {
                    ...m,
                    status: status.status,
                    content: status.content || status.error_message || '',
                    structured_data: status.structured_data || null,
                  }
                : m
            )
          );

          setIsPolling(false);
          pollingRef.current.messageId = null;

          // Refresh conversations to update last message preview
          queryClient.invalidateQueries({ queryKey: ['miles-conversations'] });
        } else {
          // Still processing, continue polling
          pollingRef.current.timeoutId = setTimeout(poll, POLLING_INTERVAL);
        }
      };

      poll();
    },
    [pollMessageStatus, queryClient]
  );

  // Stop polling on unmount
  useEffect(() => {
    const polling = pollingRef.current;
    return () => {
      if (polling.timeoutId) {
        clearTimeout(polling.timeoutId);
      }
    };
  }, []);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async ({
      message,
      conversationId,
    }: {
      message: string;
      conversationId?: string;
    }) => {
      if (!customerId) throw new Error('Customer ID is required');
      const response = await callProtectedApi(
        `${MILES_API_BASE}/customers/${customerId}/messages`,
        'post',
        {
          message,
          conversation_id: conversationId,
        },
        'Error sending message.'
      );
      return response?.data?.data as SendMilesChatMessageResponse;
    },
    onSuccess: (data) => {
      if (data) {
        // Update current conversation ID if this is a new conversation
        if (!currentConversationId) {
          setCurrentConversationId(data.conversation_id);
        }

        // Add assistant placeholder message to local state
        const placeholderMessage: MilesChatMessage = {
          message_id: data.message_id,
          role: 'assistant',
          content: '',
          status: 'processing',
          structured_data: null,
          created_at: new Date().toISOString(),
        };

        setLocalMessages((prev) => [...prev, placeholderMessage]);

        // Start polling for the response
        startPolling(data.message_id, data.conversation_id);
      }
    },
  });

  // Delete conversation mutation
  const deleteConversationMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      if (!customerId) throw new Error('Customer ID is required');
      await callProtectedApi(
        `${MILES_API_BASE}/customers/${customerId}/conversations/${conversationId}`,
        'delete',
        {},
        'Error deleting conversation.'
      );
    },
    onSuccess: (_, conversationId) => {
      // If we deleted the current conversation, clear it
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setLocalMessages([]);
      }
      // Refresh conversations
      queryClient.invalidateQueries({ queryKey: ['miles-conversations'] });
    },
  });

  // Send message handler
  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim()) return;

      setIsSending(true);
      setError(null);

      // Add user message to local state immediately
      const tempUserMessage: MilesChatMessage = {
        message_id: `temp-${Date.now()}`,
        role: 'user',
        content: message,
        status: 'completed',
        structured_data: null,
        created_at: new Date().toISOString(),
      };

      setLocalMessages((prev) => [...prev, tempUserMessage]);

      try {
        await sendMessageMutation.mutateAsync({
          message,
          conversationId: currentConversationId || undefined,
        });
      } catch {
        setError('Failed to send message. Please try again.');
        // Remove the temp message on error
        setLocalMessages((prev) =>
          prev.filter((m) => m.message_id !== tempUserMessage.message_id)
        );
      } finally {
        setIsSending(false);
      }
    },
    [currentConversationId, sendMessageMutation]
  );

  // Select conversation handler
  const selectConversation = useCallback((conversationId: string) => {
    // Stop any ongoing polling
    if (pollingRef.current.timeoutId) {
      clearTimeout(pollingRef.current.timeoutId);
      pollingRef.current.messageId = null;
    }
    setIsPolling(false);
    setCurrentConversationId(conversationId);
    setLocalMessages([]);
    setError(null);
  }, []);

  // Start new conversation handler
  const startNewConversation = useCallback(() => {
    // Stop any ongoing polling
    if (pollingRef.current.timeoutId) {
      clearTimeout(pollingRef.current.timeoutId);
      pollingRef.current.messageId = null;
    }
    setIsPolling(false);
    setCurrentConversationId(null);
    setLocalMessages([]);
    setError(null);
  }, []);

  // Delete conversation handler
  const deleteConversation = useCallback(
    async (conversationId: string) => {
      await deleteConversationMutation.mutateAsync(conversationId);
    },
    [deleteConversationMutation]
  );

  // Clear error handler
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    messages: localMessages,
    conversations: conversationsData?.conversations || [],
    presetPrompts: presetsData?.prompts || [],
    currentConversationId,
    isLoading: isLoadingConversations || isLoadingPresets || isLoadingMessages,
    isSending,
    isPolling,
    error,

    // Actions
    sendMessage,
    selectConversation,
    startNewConversation,
    deleteConversation,
    clearError,
  };
}

export default useMilesChat;
